import CloseIcon from "@mui/icons-material/CloseRounded";
import { IconButton, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ClientUserGroupMember } from "../../../../api/types/userManagementTypes";
import UserGroupDetailsContext from "./UserGroupDetailsContext";
import { removeMembersAction } from "./userGroupDetailsState";

const getColumnDefinitions = (
  onRemoveMember?: (row: ClientUserGroupMember) => void
): GridColDef<ClientUserGroupMember>[] => [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    renderCell: ({ row }) => (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">{row.name}</Typography>
        <Typography variant="caption" color="textSecondary">
          {row.email}
        </Typography>
      </Stack>
    ),
  },
  {
    field: "access",
    headerName: "Companies",
    flex: 1,
    renderCell: ({ row }) =>
      row.hasAccessToAllClients ? (
        "All"
      ) : (
        <InlineItemsList displayCount={1} items={row.access} propGetter={(item) => item.clientName} />
      ),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) =>
      onRemoveMember ? (
        <IconButton color="error" onClick={() => onRemoveMember?.(row)}>
          <CloseIcon />
        </IconButton>
      ) : null,
  },
];

interface Props {
  rows: ClientUserGroupMember[];
}

const UserGroupMembersGrid = ({ rows }: Props) => {
  const { setState, hasPermissionToManage } = useDefinedContext(UserGroupDetailsContext);

  const handleRemoveMember = useCallback(
    (row: ClientUserGroupMember) => {
      setState(removeMembersAction([row.id]));
    },
    [setState]
  );

  const columns = useMemo(
    () => getColumnDefinitions(hasPermissionToManage ? handleRemoveMember : undefined),
    [handleRemoveMember, hasPermissionToManage]
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      noRowsText="No members"
      multilineRows
      disableColumnSorting
      disableColumnReorder
      disableColumnPinning
      disableMultipleRowSelection
    />
  );
};

export default UserGroupMembersGrid;
